import { computed, onMounted, onUnmounted, ref } from 'vue';
import { columnsWidth, LIST_WIDTH } from "./columns";
const ROW_HEIGHT_DEFAULT = 71;
const ROW_HEIGHT_CONDENSED = 46;
const BREAKPOINT_MOBILE = 991; // $breakpoint-mobile
/**
 * Geometry of the data table: view mode (table or list only), row height,
 * scroll position and everything derived from it.
 *
 * The scroll handler writes three primitive refs; all margins, flags and the
 * visible range are computeds on top of them.
 */
export function useDataTableLayout(options) {
    const bodyEl = ref(null);
    const windowWidth = ref(window.innerWidth);
    const scrollTop = ref(0);
    const scrollLeft = ref(0);
    const bodyHeight = ref(0);
    const rowHeight = computed(() => options.variant.value === 'condensed' ? ROW_HEIGHT_CONDENSED : ROW_HEIGHT_DEFAULT);
    // on mobile, we show only the list, not all table columns
    const showTableColumns = computed(() => options.mode.value === 'list' ? false : windowWidth.value >= BREAKPOINT_MOBILE);
    const scrollX = computed(() => showTableColumns.value && scrollLeft.value > 0);
    const scrollY = computed(() => scrollTop.value > 0);
    const rowsMarginLeft = computed(() => showTableColumns.value ? scrollLeft.value * -1 : 0);
    const listMarginLeft = computed(() => showTableColumns.value ? scrollLeft.value : 0);
    const dataWidth = computed(() => showTableColumns.value ? columnsWidth(options.selectedColumns.value) : 0);
    const headerWidth = computed(() => showTableColumns.value ? dataWidth.value + LIST_WIDTH : 0);
    const visibleItemsCount = computed(() => Math.ceil(bodyHeight.value / rowHeight.value));
    // determine visible range by scroll offset
    const visibleFrom = computed(() => {
        const offset = Math.floor(scrollTop.value / rowHeight.value);
        return Math.min(options.maxItems.value - visibleItemsCount.value, offset);
    });
    // #activateItem automatically scrolls the table on keyboard up/down
    //   events. However, due to the "scrollIntoView" behaviour, we can't
    //   scroll lower than the last loaded item. This means that we can
    //   never retrieve additional items this way if we only fetch visible
    //   rows. => The "+1" allows fetching more.
    const visibleTo = computed(() => visibleFrom.value + visibleItemsCount.value + 1);
    function onBodyScroll() {
        const el = bodyEl.value;
        if (!el) {
            return;
        }
        scrollTop.value = el.scrollTop;
        scrollLeft.value = el.scrollLeft;
        bodyHeight.value = el.clientHeight;
    }
    function onWindowResize() {
        windowWidth.value = window.innerWidth;
    }
    function scrollToTop() {
        bodyEl.value?.scrollTo({ top: 0 });
    }
    window.addEventListener('resize', onWindowResize);
    // the body fills its container, so its height changes with the layout
    //   (window size, content above the table), not only on scroll
    const bodyResizeObserver = new ResizeObserver(() => {
        const height = bodyEl.value?.clientHeight ?? 0;
        // skips the initial notification, the height is read on mount
        if (height !== bodyHeight.value) {
            bodyHeight.value = height;
            options.onBodyResize();
        }
    });
    onMounted(() => {
        bodyHeight.value = bodyEl.value?.clientHeight ?? 0;
        if (bodyEl.value) {
            bodyResizeObserver.observe(bodyEl.value);
        }
    });
    onUnmounted(() => {
        window.removeEventListener('resize', onWindowResize);
        bodyResizeObserver.disconnect();
    });
    return {
        bodyEl,
        rowHeight,
        showTableColumns,
        scrollTop,
        scrollX,
        scrollY,
        rowsMarginLeft,
        listMarginLeft,
        dataWidth,
        headerWidth,
        visibleItemsCount,
        visibleFrom,
        visibleTo,
        onBodyScroll,
        scrollToTop,
    };
}
